
import { useState, useRef, useEffect } from 'react';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  wrapperClassName?: string;
}

/**
 * LazyImage – renders an image that is only loaded once it enters the viewport.
 * Includes a shimmering placeholder while the image is loading.
 *
 * Robustness improvements:
 * • Guard against environments where `IntersectionObserver` is unavailable.
 * • Ensure the observer is cleaned up correctly even if `imgRef.current` is null.
 * • Add a fallback to load the image immediately when the observer cannot be created.
 */
export default function LazyImage({
  src,
  alt,
  className = '',
  wrapperClassName = '',
}: LazyImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // If IntersectionObserver is not supported (e.g., older browsers or SSR),
    // fall back to loading the image immediately.
    if (typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    // Clean‑up function – always disconnect the observer if it exists.
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={imgRef} className={`relative overflow-hidden ${wrapperClassName}`}>
      {/* Shimmer placeholder */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-100 animate-shimmer">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full animate-shimmer-slide" />
        </div>
      )}

      {/* Render the actual image only when it should be in view */}
      {isInView && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          className={`transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${className}`}
        />
      )}
    </div>
  );
}
